import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';

import { useIsFocused } from '@garnish/core';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { getSharedLineItemProductName } from '@order/shared-line-item';
import { useTelemetry } from '@order/Telemetry';

import { LocationSearch, NotFoundView, TransparentModal } from '../../components';
import { useLastInteractedStoreMachine } from '../../context';
import { useNavigateBack } from '../../navigation';

// ─────────────────────────────────────────────────────────────────────────────

export const ShareLineItemLocationScreen = () => {
  const isFocused = useIsFocused();

  const isSharedLinkCustomizationEnabled = useFeatureFlag('CELS-5505-order-shared-link-customization-enabled'); // prettier-ignore

  if (!isSharedLinkCustomizationEnabled) {
    return <NotFoundView />;
  }

  if (!isFocused) {
    return null;
  }

  return <ShareLineItemLocationScreenReady />;
};

// ─────────────────────────────────────────────────────────────────────────────

const ShareLineItemLocationScreenReady = () => {
  const router = useRouter();
  const navigateBack = useNavigateBack();
  const { send: sendToLastInteractedStoreMachine } = useLastInteractedStoreMachine();
  const { track } = useTelemetry();
  const { formatMessage } = useIntl();

  const { baseProductLineageKey, lineItemSlug, sharedLineItemId } =
    useLocalSearchParams<RouteParams>();

  // ─── Derived ────────────────────────────────────────────────────────

  const sharedLineItemProductName = getSharedLineItemProductName({
    sharedLineItemSlug: lineItemSlug,
  });

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleDismiss = useCallback(() => {
    navigateBack();
  }, [navigateBack]);

  const navigateToCustomize = useCallback(() => {
    router.dismissTo({
      pathname: '/customize/[baseProductLineageKey]',
      params: {
        baseProductLineageKey,
        redirectTo: `/share/${lineItemSlug}`,
        sharedLineItemSlug: lineItemSlug,
        sharedLineItemId,
      },
    });
  }, [baseProductLineageKey, lineItemSlug, router, sharedLineItemId]);

  const navigateToLocation = useCallback<OnOpenRestaurant>(
    (context) => {
      if (!context.activeLocation) return;

      const { restaurantId, restaurantName, restaurantSlug } = context.activeLocation;

      sendToLastInteractedStoreMachine({ type: 'RESET_ACTIVE_CART_STORE' });
      sendToLastInteractedStoreMachine({
        type: 'SET_LAST_INTERACTED_MENU_STORE',
        store: { restaurantId, restaurantName, restaurantSlug },
      });

      track('share-line-item.location-change-success', {
        sharedLineItemProductName,
        sharedLineItemSlug: lineItemSlug,
        restaurantId,
        restaurantSlug,
      });
      navigateToCustomize();
    },
    [
      lineItemSlug,
      navigateToCustomize,
      sendToLastInteractedStoreMachine,
      sharedLineItemProductName,
      track,
    ],
  );

  const navigateToDeliveryLocation = useCallback<OnOpenDeliveryRestaurant>(
    (context) => {
      const { deliveryLocation } = context;

      if (!deliveryLocation) return;

      const {
        addressId,
        restaurantId,
        restaurantName,
        restaurantSlug,
        restaurantDeliveryFee,
        deliveryVendorName,
        deliveryVendorRestaurantId,
      } = deliveryLocation;

      sendToLastInteractedStoreMachine({ type: 'RESET_ACTIVE_CART_STORE' });
      sendToLastInteractedStoreMachine({
        type: 'SET_LAST_INTERACTED_MENU_STORE',
        store: {
          restaurantId,
          restaurantName,
          restaurantSlug,
          addressId,
          deliveryFee: restaurantDeliveryFee,
          vendor: deliveryVendorName,
          vendorRestaurantId: deliveryVendorRestaurantId,
        },
      });

      track('share-line-item.location-change-success', {
        addressId,
        sharedLineItemProductName,
        sharedLineItemSlug: lineItemSlug,
        restaurantId,
        restaurantSlug,
      });
      navigateToCustomize();
    },
    [
      lineItemSlug,
      navigateToCustomize,
      sendToLastInteractedStoreMachine,
      sharedLineItemProductName,
      track,
    ],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TransparentModal
      accessibilityLabel={formatMessage(messages.dialogA11yLabel)}
      size="large"
      offset="10%"
      onClose={handleDismiss}
    >
      <LocationSearch
        navigateToLocation={navigateToLocation}
        navigateToDeliveryLocation={navigateToDeliveryLocation}
        interactedLocationRestaurantSlug={undefined}
        interactedLocationDeliveryAddressId={undefined}
        isInsideModal
      />
    </TransparentModal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  dialogA11yLabel: {
    defaultMessage: 'Select a location',
    description: 'Share Line Item | Location search | Dialog a11y label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type RouteParams = {
  baseProductLineageKey: string;
  lineItemSlug: string;
  sharedLineItemId: string;
};

type LocationSearchProps = ComponentProps<typeof LocationSearch>;

type OnOpenRestaurant = NonNullable<LocationSearchProps['navigateToLocation']>;
type OnOpenDeliveryRestaurant = NonNullable<LocationSearchProps['navigateToDeliveryLocation']>;
